/**
 * Storage: where files go, how much room is left, and what is stopping an
 * upload when one is stopped.
 *
 * This page has to work when storage does not. The server answers it from a
 * cached measurement rather than probing the backend per request, precisely so
 * that a bucket which has stopped answering produces a page saying so instead
 * of a page that hangs — and the same discipline applies here: an error is
 * rendered in place of the figure it replaces, never as a spinner that never
 * resolves.
 */

import type { ReactElement } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import { queryKeys, useStorageStatus } from '@/api/queries'
import type { StorageState, StorageStatus } from '@/api/types'
import { Badge, Button, Spinner, useToast } from '@/components/ui'
import type { BadgeTone } from '@/components/ui'
import { formatCapacity, formatRelative } from '@/lib/format'

import {
  AdminPage,
  Card,
  ErrorNotice,
  Fact,
  Facts,
  Notice,
  cx,
  describeError,
  readField,
  readFlag,
  readNumber,
  readText,
} from './AdminLayout'
import storage from './StoragePage.module.css'
import styles from './admin.module.css'

// ── Reading the payload ─────────────────────────────────────────────────────

const STATE_LABEL: Record<StorageState, string> = {
  ok: 'Healthy',
  warning: 'Filling up',
  critical: 'Nearly full',
  full: 'Full',
  unknown: 'Not measured',
}

const STATE_TONE: Record<StorageState, BadgeTone> = {
  ok: 'success',
  warning: 'warning',
  critical: 'danger',
  full: 'danger',
  unknown: 'neutral',
}

const USED_CLASS: Record<StorageState, string | undefined> = {
  ok: storage.used,
  warning: storage.usedWarning,
  critical: storage.usedCritical,
  full: storage.usedFull,
  unknown: storage.used,
}

interface Antivirus {
  label: string
  tone: BadgeTone
  detail: string
}

/**
 * Antivirus is reported by some builds and not by others. Absence is stated as
 * absence rather than guessed at in either direction: "scanning is off" and
 * "this server does not say" are different answers, and only one of them is
 * safe to act on.
 */
function antivirus(status: StorageStatus): Antivirus {
  const reported = readField(status, 'antivirus') ?? readField(status, 'clamav')

  const flag = readFlag(reported) ?? readFlag(readField(reported, 'enabled'))
  const failure = readText(readField(reported, 'error'))

  if (failure !== null) {
    return { label: 'Not working', tone: 'danger', detail: failure }
  }

  if (flag === true) {
    return {
      label: 'On',
      tone: 'success',
      detail: 'An uploaded file is scanned before anyone can download it.',
    }
  }

  if (flag === false) {
    return {
      label: 'Off',
      tone: 'warning',
      detail: 'Uploads become downloadable as soon as the bytes have arrived.',
    }
  }

  return {
    label: 'Not reported',
    tone: 'neutral',
    detail: 'This server does not say whether uploads are scanned. Ask whoever runs it.',
  }
}

interface Capacity {
  used: number
  limit: number
  /** Bytes the filesystem still has, when it could be measured. */
  diskAvailable: number | null
  diskTotal: number | null
  marginRatio: number | null
  fixed: boolean
  /** used + diskAvailable, the total the bar is drawn across. */
  pool: number
}

function capacityOf(status: StorageStatus): Capacity | null {
  const limit = status.limit_bytes
  if (limit === undefined || limit <= 0) return null

  const used = status.used_bytes
  const diskAvailable = status.disk_available_bytes ?? null
  const pool = diskAvailable === null ? limit : used + diskAvailable

  return {
    used,
    limit,
    diskAvailable,
    diskTotal: status.disk_total_bytes ?? null,
    marginRatio: status.margin_ratio ?? null,
    fixed: status.fixed_limit === true,
    pool: Math.max(pool, limit),
  }
}

function percent(part: number, whole: number): number {
  if (whole <= 0) return 0
  return Math.max(0, Math.min(100, (part / whole) * 100))
}

/**
 * Every capacity figure arrives twice: the bytes, for drawing the bar, and the
 * server's own rendering of them, for reading. The rendering is the one
 * `relaii doctor` prints and `docs/storage.md` documents, so it is the one an
 * operator compares this page against — and the only one that reads back the
 * `RELAII_STORAGE_MAX_GB` they typed. `formatCapacity` is the same arithmetic,
 * for the two figures this page works out for itself.
 */
function figure(human: string | undefined, bytes: number): string {
  return human ?? formatCapacity(bytes)
}

// ── The page ────────────────────────────────────────────────────────────────

export function StoragePage(): ReactElement {
  const query = useStorageStatus()
  const queryClient = useQueryClient()
  const toast = useToast()

  // `?refresh=true` makes the server measure now and wait for the answer, which
  // is what pressing a button means and the wrong thing to do on page load.
  const remeasure = useMutation({
    mutationFn: () => api.get<StorageStatus>('/admin/storage', { refresh: true }),
    onSuccess: (fresh) => {
      queryClient.setQueryData<StorageStatus>(queryKeys.admin.storage(), fresh)
    },
  })

  const status = query.data

  const refresh = (): void => {
    remeasure.mutate(undefined, {
      onError: (error: unknown) => {
        toast.error(describeError(error, 'Storage could not be measured.'))
      },
    })
  }

  if (status === undefined) {
    return (
      <AdminPage title="Storage">
        {query.isError ? (
          <Card>
            <ErrorNotice error={query.error} title="Storage could not be read">
              <p className={styles.small}>
                Uploads are unavailable while this is true. Chat itself is unaffected.
              </p>
              <div className={styles.cellActions}>
                <Button onClick={() => void query.refetch()}>Try again</Button>
              </div>
            </ErrorNotice>
          </Card>
        ) : (
          <Card>
            <Spinner size="lg" label="Reading storage" />
          </Card>
        )}
      </AdminPage>
    )
  }

  // The backend reports a health string as well as a quota state; either can
  // say something is wrong, and whichever does is what the operator came for.
  const unhealthy = readText(readField(status, 'status')) === 'error'
  const failure =
    readText(status.error) ?? (unhealthy ? 'The storage backend did not answer.' : null)

  const checkedAt = readText(readField(status, 'checked_at'))
  const maxUpload = readNumber(readField(status, 'max_upload_bytes'))
  const writable = readFlag(readField(status, 'writable'))
  const scanning = antivirus(status)
  const capacity = capacityOf(status)

  return (
    <AdminPage
      title="Storage"
      lead="Where attachments are kept, and how much room is left for them."
      actions={
        <Button onClick={refresh} loading={remeasure.isPending}>
          Measure now
        </Button>
      }
    >
      {failure === null ? null : (
        <Notice tone="danger" title="Storage is not answering">
          <p className={styles.mono}>{failure}</p>
          <p className={styles.small}>
            Uploads and downloads fail while this is true. Messages are stored in the database and
            are unaffected.
          </p>
        </Notice>
      )}

      <Card title="Backend">
        <Facts>
          <Fact term="In use">
            {status.backend === 's3' ? 'S3-compatible object store' : 'Local disk'}
          </Fact>
          <Fact term="State">
            <Badge tone={STATE_TONE[status.state]}>{STATE_LABEL[status.state]}</Badge>
          </Fact>
          {writable === null ? null : (
            <Fact term="Writable">
              {writable ? 'Yes — a probe file was written and read back' : 'No'}
            </Fact>
          )}
          <Fact term="Measured">
            {checkedAt === null ? (
              <span className={styles.muted}>Unknown</span>
            ) : (
              <span title={checkedAt}>{formatRelative(checkedAt)}</span>
            )}
          </Fact>
        </Facts>
      </Card>

      {status.backend === 'local' ? (
        <LocalCapacity status={status} capacity={capacity} />
      ) : (
        <ObjectStore status={status} />
      )}

      <Card title="Uploads">
        <Facts>
          <Fact term="Largest file">
            {maxUpload === null ? (
              <span className={styles.muted}>Not reported</span>
            ) : (
              figure(status.max_upload_human, maxUpload)
            )}
          </Fact>
          <Fact term="Antivirus">
            <span className={styles.rowCentred}>
              <Badge tone={scanning.tone}>{scanning.label}</Badge>
            </span>
            <span className={styles.hint}>{scanning.detail}</span>
          </Fact>
        </Facts>
      </Card>
    </AdminPage>
  )
}

// ── Local disk ──────────────────────────────────────────────────────────────

function LocalCapacity({
  status,
  capacity,
}: {
  status: StorageStatus
  capacity: Capacity | null
}): ReactElement {
  const path = readText(status.path)

  if (capacity === null) {
    return (
      <Card title="Capacity">
        <div className={styles.stack}>
          <Notice tone="warning" title="The volume could not be measured">
            <p>
              relaii knows how much it has stored — {figure(status.used_human, status.used_bytes)} —
              but not how much room the volume has left, so it cannot work out a ceiling.
            </p>
          </Notice>
          <Facts>
            <Fact term="Stored">{figure(status.used_human, status.used_bytes)}</Fact>
            {path === null ? null : (
              <Fact term="Path">
                <span className={styles.mono}>{path}</span>
              </Fact>
            )}
          </Facts>
        </div>
      </Card>
    )
  }

  const marginBytes = Math.max(capacity.pool - capacity.limit, 0)
  const headroomBytes = Math.max(capacity.limit - capacity.used, 0)
  const marginPercent =
    capacity.marginRatio === null ? null : Math.round(capacity.marginRatio * 100)
  const usedShare = percent(capacity.used, capacity.limit)

  return (
    <Card title="Capacity">
      <div className={styles.stack}>
        <div>
          <div className={storage.headline}>
            <span className={storage.headlineFigure}>
              {figure(status.used_human, capacity.used)}
            </span>
            <span className={styles.muted}>
              of {figure(status.limit_human, capacity.limit)} used — {Math.round(usedShare)}%
            </span>
            <Badge tone={STATE_TONE[status.state]}>{STATE_LABEL[status.state]}</Badge>
          </div>

          {/*
            Widths are the measurement itself, so they are the one thing that
            cannot live in the stylesheet.
          */}
          <div
            className={storage.bar}
            role="img"
            aria-label={`${figure(status.used_human, capacity.used)} used of a ${figure(
              status.limit_human,
              capacity.limit,
            )} ceiling, with ${formatCapacity(marginBytes)} of the volume kept free.`}
          >
            <div
              className={cx(storage.segment, USED_CLASS[status.state])}
              style={{ width: `${String(percent(capacity.used, capacity.pool))}%` }}
            />
            <div
              className={cx(storage.segment, storage.headroom)}
              style={{ width: `${String(percent(headroomBytes, capacity.pool))}%` }}
            />
            <div
              className={cx(storage.segment, storage.margin)}
              style={{ width: `${String(percent(marginBytes, capacity.pool))}%` }}
            />
          </div>

          <ul className={storage.legend}>
            <li className={storage.legendItem}>
              <span className={cx(storage.swatch, USED_CLASS[status.state])} aria-hidden="true" />
              <span>
                <span className={storage.legendValue}>
                  {figure(status.used_human, capacity.used)}
                </span>{' '}
                <span className={storage.legendLabel}>stored</span>
              </span>
            </li>
            <li className={storage.legendItem}>
              <span className={cx(storage.swatch, storage.headroom)} aria-hidden="true" />
              <span>
                <span className={storage.legendValue}>
                  {figure(status.available_human, headroomBytes)}
                </span>{' '}
                <span className={storage.legendLabel}>still available</span>
              </span>
            </li>
            <li className={storage.legendItem}>
              <span className={cx(storage.swatch, storage.margin)} aria-hidden="true" />
              <span>
                <span className={storage.legendValue}>{formatCapacity(marginBytes)}</span>{' '}
                <span className={storage.legendLabel}>
                  {capacity.fixed ? 'outside the pinned ceiling' : 'reserved margin'}
                </span>
              </span>
            </li>
          </ul>
        </div>

        <p className={storage.rule}>
          {capacity.fixed
            ? `An operator has pinned this server to a fixed ceiling of ${figure(
                status.limit_human,
                capacity.limit,
              )}, so the margin rule does not apply and the rest of the volume is left to whatever else lives on it.`
            : `relaii never fills the disk it runs on: it keeps a margin of ${
                marginPercent === null ? '25' : String(marginPercent)
              }% free on top of whatever it stores, so a volume with ${formatCapacity(
                capacity.pool,
              )} to give yields a ceiling of ${figure(status.limit_human, capacity.limit)}.`}
        </p>

        <Facts>
          <Fact term="Stored">{figure(status.used_human, capacity.used)}</Fact>
          <Fact term="Ceiling">
            {figure(status.limit_human, capacity.limit)}
            {capacity.fixed ? <span className={styles.hint}>Pinned by the operator</span> : null}
          </Fact>
          <Fact term="Still available">{figure(status.available_human, headroomBytes)}</Fact>
          {capacity.diskAvailable === null ? null : (
            <Fact term="Free on the volume">
              {figure(status.disk_available_human, capacity.diskAvailable)}
            </Fact>
          )}
          {capacity.diskTotal === null ? null : (
            <Fact term="Volume size">{figure(status.disk_total_human, capacity.diskTotal)}</Fact>
          )}
          {readText(status.path) === null ? null : (
            <Fact term="Path">
              <span className={styles.mono}>{readText(status.path)}</span>
            </Fact>
          )}
        </Facts>
      </div>
    </Card>
  )
}

// ── S3 ──────────────────────────────────────────────────────────────────────

function ObjectStore({ status }: { status: StorageStatus }): ReactElement {
  const endpoint = readText(readField(status, 'endpoint'))
  const bucket = readText(readField(status, 'bucket'))
  const pathStyle = readFlag(readField(status, 'path_style'))
  const writable = readFlag(readField(status, 'writable'))

  return (
    <Card
      title="Object store"
      lead="Capacity is the provider's to enforce, so relaii reports no ceiling of its own here."
    >
      <div className={styles.stack}>
        <Facts>
          <Fact term="Endpoint">
            {endpoint === null ? (
              <span className={styles.muted}>Not reported</span>
            ) : (
              <span className={styles.mono}>{endpoint}</span>
            )}
          </Fact>
          <Fact term="Bucket">
            {bucket === null ? (
              <span className={styles.muted}>Not reported</span>
            ) : (
              <span className={styles.mono}>{bucket}</span>
            )}
          </Fact>
          <Fact term="Addressing">
            {pathStyle === null
              ? 'Not reported'
              : pathStyle
                ? 'Path style — endpoint/bucket/key'
                : 'Virtual host style — bucket.endpoint/key'}
          </Fact>
          <Fact term="Connectivity">
            {writable === true
              ? 'A probe object was written, read back and deleted'
              : writable === false
                ? 'The bucket refused a write'
                : 'Not reported'}
          </Fact>
          <Fact term="Stored by relaii">{figure(status.used_human, status.used_bytes)}</Fact>
        </Facts>
      </div>
    </Card>
  )
}
